import styled from '@emotion/styled';
import { Button, Dialog, DialogActions, DialogContent, Divider, Grid, Paper, Typography } from '@material-ui/core';
import Publish from '@material-ui/icons/Publish';
import Undo from '@material-ui/icons/Undo';
import { createElement, FC, Fragment, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMount } from 'react-use';
import { useRecoilValue } from 'recoil';

import { Option } from '../../../../generated/graphql';
import { calcDiscountPoint } from '../../../../utility/calcDiscountPoint';
import { useGetIsPC } from '../../../../utilityHooks/useGetIsPC';
import ObsoletedProjectTypeDialogAndButton from '../../../component/compound/ObsoletedProjectTypeDialogAndButton';
import _CreationSetList from '../../../component/compound/ProjectPreview/CreationSetList';
import _CreationSetSubTotal from '../../../component/compound/ProjectPreview/CreationSetSubTotal';
import { CreationSetTotal } from '../../../component/compound/ProjectPreview/CreationSetTotal';
import _DeliveryConditionsList from '../../../component/compound/ProjectPreview/DeliveryConditionsList';
import _OptionsList from '../../../component/compound/ProjectPreview/OptionList';
import RequiredPointSummaryDescription from '../../../component/compound/RequiredPointSummaryDescription';
import Spinner from '../../../component/compound/Spinner';
import ButtonWithIcon from '../../../component/primitive/ButtonWithIcon';
import { SCREEN_LARGE } from '../../../constant/pageWidth';
import { SPACING_HUGE, SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { context as DependencyContext } from '../../../context/dependency';
import { context as StateContext } from '../../../context/state';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';
import { editingProjectDeliveryDueDatesAtom, editingProjectStateAtom } from '../atoms/editingProjectStateAtom';
import { projectAtom, projectLoadingAtom } from '../atoms/projectAtom';
import { getOrganization } from '../domain/helperFunctions';
import {
  useGetExpressDuePointTotal,
  useGetInputtedPointTotal,
  useGetOrderTotal,
  useGetPointSubTotals,
  useGetRequiredPoints,
  useSelectedOptionTotalPoint,
} from '../hooks/useGetTotal';
import useUpdateProject from '../hooks/useUpdateProject';
import { projectHasOptionsSelector } from '../selectors/projectSelectors';
import LeftWorkflowStepper from './LeftWorkflowStepper';
import MobileOnlyWorkflowStepper from './MobileOnlyWorkflowStepper';

type Props = {
  setConfirmAlert: (confirmAlert: boolean) => void;
  forReordering?: boolean;
};

const PreviewScreen: FC<Props> = ({ setConfirmAlert, forReordering }) => {
  const availableDeliveryDueDates = useRecoilValue(editingProjectDeliveryDueDatesAtom);
  const state = useRecoilValue(editingProjectStateAtom);
  const project = useRecoilValue(projectAtom);
  const isLoading = useRecoilValue(projectLoadingAtom);
  const {
    acct,
    projectEditing,
    pricingMerchandiseOptions,
    lastAccessedOrganization,
    availableOrganizations,
  } = useContext(StateContext);
  const { updateDraftProjectAndGoToDifferentTab, setCreativeVisibility, submitProject } = useUpdateProject();
  const { organizationUsecase } = useContext(DependencyContext);

  const isPC = useGetIsPC();
  const organization = getOrganization(lastAccessedOrganization, availableOrganizations);
  const hasOption = useRecoilValue(projectHasOptionsSelector);
  const inputtedPointTotal = useGetInputtedPointTotal('preview');
  const discountTotalPoint = calcDiscountPoint(projectEditing.discountCode, inputtedPointTotal);
  const productPricing = acct.productPricing || undefined;
  const orderTotal = useGetOrderTotal();
  const pointSubTotal = useGetPointSubTotals('preview');
  const requiredPoint = useGetRequiredPoints(project?.creationSet, 'preview');
  const expressDuePointTotal = useGetExpressDuePointTotal(project?.creationSet || undefined, 'preview');
  const optionPointTotal = useSelectedOptionTotalPoint('preview');
  const isPointRequired = lastAccessedOrganization ? typeof lastAccessedOrganization.pointBalance === 'number' : false;
  const pointBalance = lastAccessedOrganization ? lastAccessedOrganization.pointBalance : null;
  const matchingFormat = state.matchingFormat;
  const projectType = project?.projectType;

  const [isOpenRequirePoint, setIsOpenRequirePoint] = useState(false);

  // 合計金額に対しての不足金額
  const shortagePoints = useMemo(() => {
    return pointBalance === null || pointBalance === undefined || !inputtedPointTotal
      ? 0
      : pointBalance - (inputtedPointTotal - discountTotalPoint);
  }, [pointBalance, discountTotalPoint, inputtedPointTotal]);

  const filteredPricingMerchandiseOptions = (pricingMerchandiseOptions.availablePricingMerchandiseOptions
    .filter((pricingMerchandiseOption) =>
      pricingMerchandiseOptions.selectedPricingMerchandiseOptions.includes(pricingMerchandiseOption.id),
    )
    .map((option) => option.toJSON()) as unknown) as ReadonlyArray<Option>;

  const handleOnClickSubmitButton = (pointTotal: number) => {
    setConfirmAlert(false);
    setTimeout(async () => {
      await submitProject({ state, pointTotal });
    }, 0);
  };

  useMount(async () => {
    await organizationUsecase.refreshPointBalance(organization.id);
    // DidMount時にcanOpenOfferWhenDisagreedに従って処理する
    // true = 公開に同意せずオファーを出せる
    // false = 基本的に公開される
    // https://kaizen.qiita.com/shared/items/1451b2aa8c2931cee0f4#ui-5
    if (
      project &&
      project.organization &&
      project.organization.features &&
      project.organization.features.canOpenOfferWhenDisagreed !== undefined
    ) {
      await setCreativeVisibility(project.organization.features.canOpenOfferWhenDisagreed);
    }
  });

  const isOldProjectType = useMemo(() => {
    return projectType && projectType.version <= 1;
  }, [projectType]);

  if (isLoading || !state) return <Spinner fullHeight />;

  return (
    <Root>
      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={isPC ? 4 : 2}>
        <LeftWorkflowStepper
          step="preview"
          updateDraftAndGoToTargetStep={(targetStep) =>
            updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'preview', targetStep })
          }
        />
        <Grid item xs={12} sm={12} md={12} lg={9}>
          {isLoading || !projectType ? (
            <Spinner />
          ) : (
            <>
              <Heading variant="h5">
                <FormattedMessage
                  id="project_editing_page.preview.screen_title"
                  values={{ projectTypeTitle: projectType.title }}
                />
              </Heading>
              <SupportingText variant="body2" color="textSecondary">
                <FormattedMessage
                  id={
                    forReordering
                      ? 'project_editing_page.for_preview.description_for_reordering'
                      : 'project_editing_page.for_preview.description'
                  }
                />
              </SupportingText>
              {state.creationSet !== null ? (
                <>
                  <Container>
                    {project.creationSet && (
                      // CreationSets が2つ以上存在する場合にのみ見出しを表示（v1プロジェクト用
                      <CreationSetCount variant="h6">
                        <FormattedMessage id="creation_set_list.creation_set_list_item.title" />
                      </CreationSetCount>
                    )}

                    {/* CreationSet */}
                    <SectionHeading variant="subtitle1">
                      <FormattedMessage id="project_editing_page.for_preview.section_heading.creative_formats" />
                    </SectionHeading>
                    <CreationSetList
                      creationSet={state.creationSet}
                      productPricing={productPricing}
                      isPointRequired={isPointRequired}
                    />

                    {/* Options */}
                    {hasOption && (
                      <>
                        <SectionHeading variant="subtitle1">
                          <FormattedMessage id="project_editing_page.for_preview.section_heading.options" />
                        </SectionHeading>
                        <OptionsList
                          productPricing={productPricing}
                          isPointRequired={isPointRequired}
                          pricingMerchandiseOptions={filteredPricingMerchandiseOptions}
                          creationSet={state.creationSet}
                        />
                      </>
                    )}

                    {/* Due Date & Matching Format Table */}
                    {state.creationSet && availableDeliveryDueDates.length ? (
                      <>
                        <FlexContainer>
                          <SectionHeading variant="subtitle1">
                            <FormattedMessage id="project_editing_page.for_preview.section_heading.delivery_conditions" />
                          </SectionHeading>
                        </FlexContainer>

                        <DeliveryConditionsList
                          productPricing={productPricing}
                          isPointRequired={isPointRequired}
                          availableDeliveryDueDates={availableDeliveryDueDates}
                          deliveryDueDays={state.creationSet.deliveryDueDays ?? null}
                          expressDuePointTotal={expressDuePointTotal}
                          matchingFormat={matchingFormat}
                        />
                      </>
                    ) : null}

                    <Divider />

                    {/* Sub Total && CreationSet Amount */}
                    <Grid container direction="row" justifyContent="flex-end" alignItems="stretch">
                      <Grid item xs={undefined} sm={undefined} md={6} lg={4} />
                      <Grid item xs={12} sm={12} md={12} lg={8}>
                        <CreationSetSubTotal
                          productPricing={productPricing}
                          isPointRequired={isPointRequired}
                          subTotal={pointSubTotal === null ? undefined : pointSubTotal}
                          requiredPoint={requiredPoint}
                          orderTotal={orderTotal}
                          expressDuePointTotal={expressDuePointTotal}
                          optionPointTotal={optionPointTotal}
                          creationSetAmount={state.creationSet.amount}
                          projectType={projectType}
                        />
                      </Grid>
                    </Grid>
                  </Container>

                  {/* Total */}
                  {isPointRequired && productPricing && pointBalance !== null && pointBalance !== undefined ? (
                    <ContainerLast>
                      <Grid container direction="row" justifyContent="flex-end" alignItems="stretch">
                        <Grid item xs={undefined} sm={undefined} md={6} lg={4} />
                        <Grid item xs={12} sm={12} md={12} lg={8}>
                          <CreationSetTotal
                            pointBalance={pointBalance}
                            productPricing={productPricing}
                            isPointRequired={isPointRequired}
                            total={inputtedPointTotal ? inputtedPointTotal : undefined}
                          />
                        </Grid>
                        <Grid item xs={undefined} sm={undefined} md={4} lg={4} />
                        {/*TODO: 運用で利用していないので利用再開まで非表示*/}
                        {/*<Grid item xs={12} sm={12} md={12} lg={8}>*/}
                        {/*  <DiscountCode />*/}
                        {/*</Grid>*/}
                        <Grid item xs={12} sm={12} md={8} lg={8}>
                          <RequiredPointSummaryDescription
                            pointBalance={pointBalance}
                            productPricing={productPricing}
                          />
                        </Grid>
                      </Grid>
                      <Badges>
                        <BadgesFlex>
                          <a href="https://trustlock.co" target="_brank" rel="noreferrer noreferrer">
                            <Image src="/assets/trustlock/guaranteed-safe-checkout.png" />
                          </a>
                          <a href="https://trustlock.co" target="_brank" rel="noreferrer noreferrer">
                            <Image src="/assets/trustlock/satisisfaction-guaranteed-badge-icon.png" />
                          </a>
                        </BadgesFlex>
                        <a href="https://trustlock.co" target="_brank" rel="noreferrer noreferrer">
                          <Image src="/assets/trustlock/stripe.svg" />
                        </a>
                      </Badges>
                    </ContainerLast>
                  ) : null}
                </>
              ) : null}
            </>
          )}

          <MobileOnlyWorkflowStepper
            step="preview"
            updateDraftAndGoToTargetStep={(targetStep) =>
              updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'preview', targetStep })
            }
          />

          {!isLoading && project?.creativeBriefDefinition && project?.creativeBriefValues ? (
            <Actions>
              {/* Creative Visibility */}
              {/*TODO: issueであげられていますがそもそも仕様が確定していなかったので、コメントアウトにし、別途対応。issue: https://github.com/kaizenplatform/hugot/issues/1915*/}
              {/* <CreativeVisibility canOpenOfferWhenDisagreed={canOpenOfferWhenDisagreed} /> */}
              <Buttons>
                {hasOption ? (
                  <ButtonWithIcon
                    icon={Undo}
                    color="default"
                    variant="contained"
                    onClick={() =>
                      updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'preview', targetStep: 'option' })
                    }
                  >
                    <FormattedMessage id="project_editing_page.preview_setting.buttons.back" />
                  </ButtonWithIcon>
                ) : (
                  <ButtonWithIcon
                    icon={Undo}
                    color="default"
                    variant="contained"
                    onClick={() =>
                      updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'preview', targetStep: 'brief' })
                    }
                  >
                    <FormattedMessage id="project_editing_page.option_setting.buttons.back" />
                  </ButtonWithIcon>
                )}
                {isOldProjectType && <ObsoletedProjectTypeDialogAndButton />}
                {!isOldProjectType && lastAccessedOrganization?.businessRegion.key === 'jp' && (
                  <>
                    {(!isPointRequired || (isPointRequired && shortagePoints >= 0)) && (
                      <ButtonWithIcon
                        icon={Publish}
                        color="primary"
                        variant="contained"
                        disabled={isPointRequired && shortagePoints < 0}
                        onClick={() => handleOnClickSubmitButton(inputtedPointTotal ? inputtedPointTotal : 0)}
                      >
                        <FormattedMessage id="project_editing_page.preview.buttons.submit" />
                      </ButtonWithIcon>
                    )}
                    {!isOldProjectType && isPointRequired && shortagePoints < 0 && productPricing && (
                      <ButtonWithIcon
                        icon={Publish}
                        color="primary"
                        variant="contained"
                        disabled={isPointRequired && shortagePoints < 0}
                        onClick={() => handleOnClickSubmitButton(inputtedPointTotal ? inputtedPointTotal : 0)}
                      >
                        <FormattedMessage id="project_editing_page.preview.buttons.submit" />
                      </ButtonWithIcon>
                    )}
                  </>
                )}
                {!isOldProjectType && lastAccessedOrganization?.businessRegion.key !== 'jp' && (
                  <>
                    <ButtonWithIcon
                      icon={Publish}
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        if (
                          isPointRequired &&
                          shortagePoints < 0 &&
                          lastAccessedOrganization?.businessRegion.key !== 'jp'
                        ) {
                          setIsOpenRequirePoint(true);
                        } else {
                          handleOnClickSubmitButton(inputtedPointTotal ? inputtedPointTotal : 0);
                        }
                      }}
                    >
                      <FormattedMessage id="project_editing_page.preview.buttons.submit" />
                    </ButtonWithIcon>
                    <Dialog open={isOpenRequirePoint}>
                      <DialogContent>
                        <Typography variant="subtitle1" color="textPrimary">
                          <FormattedMessage id="project_editing_page.preview.require_point.modal.text" />
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          onClick={() => {
                            setIsOpenRequirePoint(false);
                          }}
                        >
                          <FormattedMessage id="obsoleted_project_type_dialog.close" />
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </>
                )}
              </Buttons>
              {isPointRequired && shortagePoints < 0 ? (
                <ShortageMessage>
                  <FormattedMessage id="project_editing_page.point_shortage_message" />
                </ShortageMessage>
              ) : null}
            </Actions>
          ) : null}
        </Grid>
      </Grid>
    </Root>
  );
};

const Root = styled('section')`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin-top: ${SPACING_HUGE}px;
  margin-right: auto;
  margin-left: auto;
  padding-bottom: ${SPACING_UNIT * 12}px;

  ${mq('sp')} {
    margin-top: ${SPACING_REGULAR}px;
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
    padding-bottom: ${SPACING_UNIT * 6}px;
  }
`;

const CreationSetCount = styled(Typography)`
  ${flexibleLayoutContainer({
    direction: 'column',
  })}
  margin-bottom: ${SPACING_REGULAR}px;
  font-weight: 700;

  ${mq('sp')} {
    font-size: 21px;
    margin-bottom: ${SPACING_UNIT * 3}px;
  }
`;

const Heading = styled(Typography)`
  margin-bottom: ${SPACING_UNIT}px;

  ${mq('sp')} {
    font-size: 1.3rem;
  }
`;

const SupportingText = styled(Typography)`
  margin-bottom: ${SPACING_REGULAR}px;
`;

const Container = styled(Paper)`
  margin-bottom: ${SPACING_HUGE}px;
  padding: ${SPACING_REGULAR}px;
`;

const ContainerLast = styled(Paper)`
  margin-bottom: 20px;
  padding: ${SPACING_REGULAR}px;

  ${mq('sp')} {
    margin-bottom: ${SPACING_HUGE}px;
  }
`;

const CreationSetSubTotal = styled(_CreationSetSubTotal)`
  margin-top: ${SPACING_REGULAR}px;
`;

const SectionHeading = styled(Typography)`
  font-weight: bold;
  margin-bottom: ${SPACING_UNIT * 2}px;
`;

const FlexContainer = styled('div')`
  ${flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'space-between',
  })}
`;

const CreationSetList = styled(_CreationSetList)`
  margin-bottom: ${SPACING_LARGE}px;
`;

const OptionsList = styled(_OptionsList)`
  margin-bottom: ${SPACING_LARGE}px;
`;

const DeliveryConditionsList = styled(_DeliveryConditionsList)`
  margin-bottom: ${SPACING_LARGE}px;
  ${mq('tab')}: {
    margin-bottom: 10px;
  }
`;

const Actions = styled('div')`
  ${flexibleLayoutContainer({
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  })}
  flex-direction: column;
`;

const Buttons = styled('div')`
  ${flexibleLayoutContainer({
    justifyContent: 'flex-end',
    wrap: 'wrap',
  })}
  > button {
    margin: ${SPACING_UNIT}px 0 0 ${SPACING_UNIT}px;
  }

  ${mq('tab')} {
    width: 100%;
    flex-direction: column-reverse;

    > button {
      margin: ${SPACING_UNIT}px 0 0 0;
      width: 100%;
    }
  }
`;

const Badges = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  & > * {
    margin-right: 8px;
  }

  ${mq('sp')} {
    flex-direction: column;
    align-items: flex-end;
    margin-top: 20px;

    a {
      margin-right: 0;
    }
  }
`;

const BadgesFlex = styled('div')`
  ${mq('sp')} {
    margin-right: 0;
    margin-bottom: 10px;

    > a {
      &:first-of-type {
        margin-right: 10px;
      }
    }
  }
`;

const Image = styled('img')`
  width: 120px;
`;

const ShortageMessage = styled(Typography)`
  margin-top: 8px;
  color: #ff7170;
`;

export default PreviewScreen;
